import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Filter, Calendar, Users, MousePointerClick, Clock, Globe, Mail, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function EventReportsPage() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [visitors, setVisitors] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalVisitors, setTotalVisitors] = useState(0);

    useEffect(() => {
        if (eventId) {
            fetchEvent();
            fetchStats();
            fetchVisitors();
        }
    }, [eventId, startDate, endDate, currentPage]);

    const fetchEvent = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${eventId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setEvent(data);
            }
        } catch (error) {
            console.error('Error fetching event:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);

            const response = await fetch(`${API_URL}/events/${eventId}/visitors/stats?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setStats(data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchVisitors = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            params.append('limit', pageSize);
            params.append('offset', (currentPage - 1) * pageSize);

            const response = await fetch(`${API_URL}/events/${eventId}/visitors?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setVisitors(data.visitors);
                setTotalVisitors(data.total);
            } else {
                setError('Failed to load visitors');
            }
        } catch (error) {
            console.error('Error fetching visitors:', error);
            setError('Failed to load visitors');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const params = new URLSearchParams();
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            params.append('limit', '10000'); // Export all

            const response = await fetch(`${API_URL}/events/${eventId}/visitors?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                
                // Convert to CSV
                const headers = [
                    'Visit Date', 'Session ID', 'Visitor ID', 'First Name', 'Last Name', 
                    'Email', 'Phone', 'IP Address', 'Device Type', 'Browser', 'OS',
                    'Country', 'Region', 'City', 'Referrer', 'UTM Source', 'UTM Medium',
                    'UTM Campaign', 'Time on Page (sec)', 'Page Views', 'Appointment Created',
                    'Voicebox Email ID', 'Voicebox Campaign ID'
                ];
                
                const rows = data.visitors.map(v => [
                    new Date(v.visit_started_at).toLocaleString(),
                    v.session_id,
                    v.visitor_id || '',
                    v.first_name || '',
                    v.last_name || '',
                    v.email || '',
                    v.phone || '',
                    v.ip_address || '',
                    v.device_type || '',
                    v.browser_name ? `${v.browser_name} ${v.browser_version || ''}`.trim() : '',
                    v.os_name ? `${v.os_name} ${v.os_version || ''}`.trim() : '',
                    v.country_code || '',
                    v.region || '',
                    v.city || '',
                    v.referrer_url || '',
                    v.utm_source || '',
                    v.utm_medium || '',
                    v.utm_campaign || '',
                    v.time_on_page_seconds || '',
                    v.page_views || 1,
                    v.appointment_created ? 'Yes' : 'No',
                    v.voicebox_email_id || '',
                    v.voicebox_campaign_id || ''
                ]);

                const csv = [
                    headers.join(','),
                    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                ].join('\n');

                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `event-visitors-${event?.name || eventId}-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error exporting data:', error);
            alert('Failed to export data');
        }
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading reports...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/events')}
                        className="p-2 hover:bg-gray-100 rounded"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            Event Reports
                        </h1>
                        {event && (
                            <p className="text-gray-600 mt-1">{event.name}</p>
                        )}
                    </div>
                </div>
                <button
                    onClick={handleExport}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    <Download size={18} />
                    Export CSV
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-4 mb-4">
                    <Filter size={20} className="text-gray-500" />
                    <h2 className="text-lg font-semibold">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => {
                                setEndDate(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setCurrentPage(1);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            {stats && stats.summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Visits</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {stats.summary.total_visits || 0}
                                </p>
                            </div>
                            <Users className="text-blue-500" size={32} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Unique Visitors</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {stats.summary.unique_visitors || 0}
                                </p>
                            </div>
                            <Globe className="text-green-500" size={32} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Appointments Created</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {stats.summary.appointments_created || 0}
                                </p>
                            </div>
                            <Calendar className="text-purple-500" size={32} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">From Email Campaigns</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {stats.summary.from_email_campaign || 0}
                                </p>
                            </div>
                            <Mail className="text-orange-500" size={32} />
                        </div>
                    </div>
                </div>
            )}

            {/* Additional Stats */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4">Top Referrers</h3>
                        {stats.top_referrers && stats.top_referrers.length > 0 ? (
                            <ul className="space-y-2">
                                {stats.top_referrers.map((ref, idx) => (
                                    <li key={idx} className="flex justify-between items-center">
                                        <span className="text-sm text-gray-700 truncate flex-1 mr-2">
                                            {ref.referrer_url || 'Direct'}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">
                                            {ref.visit_count}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500">No referrer data available</p>
                        )}
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold mb-4">Top Campaign Sources</h3>
                        {stats.top_utm_sources && stats.top_utm_sources.length > 0 ? (
                            <ul className="space-y-2">
                                {stats.top_utm_sources.map((source, idx) => (
                                    <li key={idx} className="flex justify-between items-center">
                                        <span className="text-sm text-gray-700 truncate flex-1 mr-2">
                                            {source.utm_source}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">
                                            {source.visit_count}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500">No campaign data available</p>
                        )}
                    </div>
                </div>
            )}

            {/* Visitors Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold">Visitor Details</h2>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Loading visitors...</div>
                ) : visitors.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No visitors found</div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visit Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visitor</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Appointment</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {visitors.map((visitor) => (
                                        <tr key={visitor.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatDate(visitor.visit_started_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {visitor.first_name || visitor.last_name ? (
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {[visitor.first_name, visitor.last_name].filter(Boolean).join(' ') || 'Anonymous'}
                                                        </div>
                                                        <div className="text-gray-500 text-xs">
                                                            {visitor.session_id?.substring(0, 8)}...
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-500">Anonymous</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {visitor.email && (
                                                    <div className="flex items-center gap-1 text-gray-900">
                                                        <Mail size={14} />
                                                        {visitor.email}
                                                    </div>
                                                )}
                                                {visitor.phone && (
                                                    <div className="flex items-center gap-1 text-gray-600 mt-1">
                                                        <Phone size={14} />
                                                        {visitor.phone}
                                                    </div>
                                                )}
                                                {!visitor.email && !visitor.phone && (
                                                    <span className="text-gray-400">No contact info</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {visitor.city && visitor.country_code ? (
                                                    <div>
                                                        {visitor.city}, {visitor.country_code}
                                                    </div>
                                                ) : visitor.country_code ? (
                                                    visitor.country_code
                                                ) : (
                                                    <span className="text-gray-400">Unknown</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {visitor.device_type && (
                                                    <div>
                                                        <div className="font-medium">{visitor.device_type}</div>
                                                        {visitor.browser_name && (
                                                            <div className="text-xs text-gray-500">
                                                                {visitor.browser_name}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {visitor.utm_source ? (
                                                    <div>
                                                        <div className="font-medium">{visitor.utm_source}</div>
                                                        {visitor.utm_campaign && (
                                                            <div className="text-xs text-gray-500">
                                                                {visitor.utm_campaign}
                                                            </div>
                                                        )}
                                                        {visitor.voicebox_email_id && (
                                                            <div className="text-xs text-blue-600 mt-1">
                                                                📧 Email Campaign
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : visitor.referrer_url ? (
                                                    <div className="text-xs text-gray-600 truncate max-w-xs">
                                                        {visitor.referrer_url}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">Direct</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatDuration(visitor.time_on_page_seconds)}
                                                {visitor.page_views > 1 && (
                                                    <div className="text-xs text-gray-500">
                                                        {visitor.page_views} pages
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {visitor.appointment_created ? (
                                                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                                                        Yes
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">No</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination */}
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                            <div className="text-sm text-gray-700">
                                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalVisitors)} of {totalVisitors} visitors
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage(p => p + 1)}
                                    disabled={currentPage * pageSize >= totalVisitors}
                                    className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

